import React from 'react';
import {Link} from "react-router-dom";
import '../App.css';
import thesis from '../images/thesis.png';
import figjam from '../images/figjam.png';
import land from '../images/landing.png';
import start from '../images/starting.png';



function Thesis(props) {
  return (
    <div className="caseify">
      <div><Link to="/" id="back">(back)</Link></div>
      <div> </div>
      <div id="title">Senior Research Thesis - UC Student Designers</div>
      <img src={thesis} alt="UC Student Designers cover graphic"/>
      <div id="table">
        <div id="tablecol">
          <div id="tablerow">
            <div id="casetitle">About</div>
            <div id="casetext">A collective narrative based on conversations with 40 UC San Diego design students, past and present.</div>
          </div>
          <div id="tablerow">
            <div id="casetitle">Topics</div>
            <div id="casetext">Design education, collective narrative, student experiences, mental health, HCI, UX </div>
          </div>
        </div>
        <div id="tablecol">
          <div id="tablerow">
            <div id="casetitle">Roles</div>
            <div id="casetext">Researcher, UX Designer, Author, Developer</div>
          </div>
          <div id="tablerow">
            <div id="casetitle">Advisor</div>
            <div id="casetext">Philip Guo, Associate Professor of Cognitive Science at UC San Diego</div>
          </div>
        </div>
        <div id="tablecol">
          <div id="tablerow">
            <div id="casetitle">Audience</div>
            <div id="casetext">UC San Diego design students (current, graduated, and prospective), students who attended college during the COVID-19 pandemic.</div>
          </div>
          <div id="tablerow">
            <div id="casetitle">Timeline</div>
            <div id="casetext">July 2021 - May 2022</div>
          </div>
        </div>
         <div id="tablerow">
            <div id="casetitle">Link to website</div>
            <a id="casetext" href="https://ucstudentdesigners.github.io/" target="_blank" rel="noopener noreferrer">
            https://ucstudentdesigners.github.io/</a>
          </div>
        <div id="tablerow">
            <div id="casetitle">Context</div>
            <div id="casetext">It’s so easy to feel behind everyone else, trapped in comparisons, unprepared for jobs, and anxious about the future when your present is ambiguous. 
            These feelings are heavy, but who do you talk to? Your parents who have no idea what UX Design is? A non-anonymous discussion on Canvas? It’s so easy to take the blame 
            for all these problems, but talking to other students and sharing your own experience reveals that these aren’t isolated incidents. You’re not alone.</div>
        </div>
        <div id="tablerow">
            <div id="casetitle">Goals</div>
            <div id="casetext">To validate and give visibility to the struggles and concerns that design students face but don’t often have the space to share. To be a resource for 
            current and future design students to process their journey and connect to the design community at UCSD.</div>
        </div>
        <div id="tablerow">
            <div id="casetitle">Impact</div>
            <div id="casetext">Since launch, more students have reached out to me to share their own design journey. My research can’t resolve the concerns about the design curriculum or 
            the demands of the industry, but my research has brought comfort and clarity to students, who in turn show me the importance of designing for the communities that matter to me. 
            I’ve learned that genuine conversations can be a vessel for impact, and I want to continue designing with sincerity. That will be my source for innovation moving forward.</div>
        </div>
      </div>

      <p>.・。.・゜✭・.・✫・゜・。.</p>

      <div id="tablerow">
        <div id="casetitle">Research</div>
          <p>My research goal was to get diverse perspectives - from transfers, international students, 1-5th years, and alums - and gather qualitative data. My advisor helped me share an 
          interest form with other Cogs professors who then shared it in their classes. I connected with 40 students who shared their stories and answered some of my questions, like:</p>

          <ul>  
          <li>Of all the majors and schools in the world, how did you end up majoring in design at UCSD?</li>
          <li>How would you describe your experience as a design student?</li>
          <li>What kind of impact do you want to have?</li>
          <li>What keeps you motivated to stay in the design field?</li>
          <li>What did you struggle with?</li>
          <li>If you had a magic wand, what would you change about your college experience?</li>
          <li>What matters to you?</li>
          <li> What does support look like to you?</li>
          </ul>

          <p>Afterwards, I created an affinity diagram to identify recurring themes in the design student experience. </p>

          <img src={figjam} id="art" alt="Affinity diagram of research insights"/>
          <p></p>

          <p>The next challenge was determining what to make of these insights. 
          Around 43% of interviewed students expressed interest in mentorship from senior students and alumni who understand the challenges of being a design student and found ways to overcome them. 
          This is especially important at a large public university like UCSD because there’s a sense of competition and scarcity over resources like mentorship and students might not know what resources 
          are available to them due to “hidden curriculum” that isn’t taught formally in class but passed between students.</p>

          <p>While I considered facilitating a mentorship program as a direct solution to this need, I realistically would not have the bandwidth to maintain such a program after graduation. 
          The idea of a collective narrative derived from my interests in memoirs and journaling, where simply documenting and sharing one’s story can be enough. It won’t have the solution to the 
          toughest problems out there, but it takes the first step in acknowledging that they exist and impact the entire community.</p>
      </div>

      <div id="tablerow">
        <div id="casetitle">Writing</div>
        <p>Before I wrote each chapter, I created guidelines for myself to ensure that I was writing with intention.</p>

        <p2><u>Impact</u></p2>
          <p><i>Validate the voices of students and acknowledge their courage to speak up and reach out for help.</i> I tell each student I’ve chatted with that I’m thankful for their time and their vulnerability. 
          There’s so many compassionate, kind, and aspirational students out there who can create an amazing impact in the design community and in the world. For the hundreds of other students who I’ve yet to connect with, 
          I hope that they know that their stories and experiences are worthy of being shared and processed. I believe in the light that the students hold for themselves and for each other.</p>
          <p><i>Acknowledge the current barriers to seeking help, developing skills, making professional progress, reaching goals, and maintaining wellbeing. </i> There is nothing being done to tell students that their struggles 
          are not because of what they lack - productivity, determination, passion, care, qualifications, etc. The obstacles are buried in educational bureaucracy, social competition, and other things that people before you have 
          decided. There is no life plan for success or for completing a design education.</p>
        <p2><u>Tone</u></p2>
          <p><i>Kind, compassionate, and encouraging. </i>The quarter system is all about fast pacing. Meeting deadlines under time pressure, otherwise you’re too late. No one ever goes out of the way to tell you that you’re doing well 
          and that you’re enough, so why not start now. It’s not your fault. You are doing the best with what you have.</p>
          <p><i>Respectful.</i> You are incorporating other people’s stories into this. This is bigger than you. Remember to be respectful of the people who were vulnerable with you. Don’t talk down on students or offer unsolicited advice like a broken record.</p>
          <p><i>First Person, I Statements.</i> Tell the story from our perspective. Use I statements for quotes. We are inclusive to the reader. Plural. Collective. I is singular. There is something luminating about reading an I statement and feeling like that could’ve been you too.</p>
      </div>

       <div id="tablerow">
            <div id="casetitle">Design</div>
              <p>The concept of a free website that reads like an e-book came to me after chatting with UW alum Sara Tieu, who shared her insights from designing a health and wellness resource for 
              design students at UW. Given the natural separation of thematic topics in my research, I created a “chapter” for each one. The pagination breaks up the content, so it’s easier for students 
              to read in shorter bouts. A digital book interface would also feel more approachable.</p>
      </div>

          <div id="tablerow">
            <img id="art" src={land} alt="Landing page of UC Student Designers"/>
          </div>
          <div id="tablerow">
            <img id="art" src={start} alt="Chapter page of UC Student Designers"/>
          </div>
      
      <div id="tablerow">
            <div id="casetitle">Development</div>
              <p>Accessibility was my top priority because I didn’t want paywalls, sign ups, or jumbled URLs to prevent students from viewing or finding my work. I used React Native to create this multi-paged site and used Github Pages to host this website. </p>
      </div>

      <div id="tablerow">
            <div id="casetitle">Marketing</div>
              <p>Primary goals were to cast a wide net to increase exposure of my research and leave crumbs for future students to find my work. I found that the most effective way for widespread marketing was asking professors 
              to share it with their students, which makes sense. Students are already checking their emails and Canvas announcements anyway. I also shared my work on the UCSD Design Community Facebook page, UCSD subreddit, 
              Design Co Discord, and LinkedIn, so any student who searches “design, UX, UCSD,” etc. can find my research even after I graduate and can’t pester professors to plug a link to my work in their syllabi. </p>
      </div>

      <div id="tablerow">
            <div id="casetitle">Epilogue</div>
              <p>Now and then, I’ll find these messages in my inbox, and my heart just explodes with warmth. I’m incredibly thankful to everyone who took the time to read my research and reach out with their stories. 
              It reminds me that I might always feel insecure and inexperienced, but that doesn’t detract from my potential to create impact and design meaningful things.</p>
              <p><i>"This is super random, and I am not the type to reach out to people like this but I just wanted to simply let you know that I really enjoyed reading through UC Student Designers. 
              It was super well-written, and it definitely touched my heart with my own concerns while inspiring me for future endeavors. Thank you so much for creating it and making a difference! :) "</i></p>
              <p><i>"I'm currently taking COGS 127 with Professor Philip Guo and as part of our assignment, we read your "UC Student Designers" project. Your story really hit home. Much like the design students you interviewed, 
              I also struggle with impostor syndrome and the ever-looming first-gen fear of not being able to find a job after college and becoming "unsuccessful"."</i></p>
              <p><i>"I LOVE it!! I read the whole website and shared it with other people who aren’t even in UCSD and they wish there was something like this beyond school!"</i></p>
              <p><i>"Woah this is really cool, I didn’t realize how many students were in the same boat as me. Thank you for all the resources and help you’ve given me so far, I appreciate it a lot!"</i></p>
              <p><i>"I also started at UCSD as a psychology major but moved to cognitive science after realizing that psychology wasn’t the appropriate fit for me. “Take care of yourself first, even if it’s the tiny things,” you advised, 
              and that hit me hard. Though it may appear simple, many people struggle with self-care. Starting college during the epidemic made me feel as if I was losing out on the best years of my life. My lack of experience in design made me feel 
              inferior when compared to others. Even though I understand that everyone is different, I couldn't help but compare myself to others and question what I was doing with my life. However, as you stated, “this is our first time living this life” 
              thus we aren’t required to “know all the answers to our questions, nor the outcomes of our goals.” The website is informative and inspiring. I appreciate everything you’ve done. 
              Thank you so much for taking the time to develop this fantastic site, from which I have received a great deal of personal and academic knowledge. Congratulations on completing college, and best wishes for the future."</i></p>
      </div>

      <p>.・。.・゜✭・.・✫・゜・。.</p>

    </div>
  );
}

export default Thesis;

